import { UserCoverageSummary } from '@ampsec/platform-client';
import { CustomTooltip } from 'Components/atoms';
import { formatEmailsForDisplay, normalizeEmailList } from 'Core-utils/email';
import { ToolingCoverageTooltip } from 'Components/molecules/ToolingCoverageTooltip';
import { USERS_DASHBOARD_ROUTE } from 'Core-utils/routes';
import { Link } from 'react-router-dom';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import { formatCsvTooltipFields, type CsvTooltipField } from '../../AgGridBase/exportCsv';

interface NameCellRendererProps {
  data: UserCoverageSummary;
}

/**
 * The tooltip rows the name cell renders: the active user's email(s), or the status / last check-in
 * of an inactive (or synthetic unassigned) row. Shared with the CSV export so both show the same
 * values; a group with no rows exports as an empty cell.
 */
export function getNameTooltipFields(row: UserCoverageSummary): CsvTooltipField[] {
  if (row.active) {
    return normalizeEmailList(row.email).map((email) => ({ label: email }));
  }
  const lastCheckinTs = row.assets?.[0]?.lastCheckinTs;
  return [
    { label: 'Status', value: 'INACTIVE' },
    { label: 'Last Check in', value: lastCheckinTs ? DateTimeFormatter(lastCheckinTs) : 'NEVER' },
  ];
}

/**
 * The CSV tooltip cell of the name column — the same rows the tooltip renders (emails for an active
 * user, status / last check-in otherwise); an active user's emails export as their bare values.
 */
export function getNameTooltipExport(row: UserCoverageSummary): string {
  return formatCsvTooltipFields(getNameTooltipFields(row));
}

const NameCellRenderer = ({ data: row }: NameCellRendererProps) => {
  const displayName = row.firstName ? `${row.firstName} ${row.lastName}` : row.firstName ?? row.name;

  if (row.active) {
    return (
      <CustomTooltip label={formatEmailsForDisplay(row.email)} tooltipClassName="custom-tooltip--email">
        <Link className="text-decoration-none ag-findings-grid__link" to={`${USERS_DASHBOARD_ROUTE}/${row.id}`}>
          {displayName}
        </Link>
      </CustomTooltip>
    );
  }

  return (
    <ToolingCoverageTooltip>
      <ToolingCoverageTooltip.Trigger>{displayName}</ToolingCoverageTooltip.Trigger>
      {getNameTooltipFields(row).map((field) => (
        <ToolingCoverageTooltip.Row key={field.label} label={field.label} value={field.value} />
      ))}
    </ToolingCoverageTooltip>
  );
};

export default NameCellRenderer;
